import React from "react";
import { motion } from "framer-motion";
import { DollarSign, TrendingUp, Award, BarChart3, PieChart as PieChartIcon } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

interface RevenueReportProps {
  dateFrom?: string;
  dateTo?: string;
  data: {
    byMonth: { month: string; totalCents: number; count: number }[];
    byPracticeArea: { practiceArea: string; totalCents: number; count: number }[];
  } | null;
  loading: boolean;
}

const PRACTICE_AREA_COLORS: Record<string, string> = {
  real_estate: "#06b6d4",
  immigration: "#10b981",
  litigation: "#3b82f6",
  corporate: "#8b5cf6",
  family: "#ec4899",
  criminal: "#ef4444",
  wills_estates: "#f59e0b",
  other: "#64748b",
};

const PRACTICE_AREA_LABELS: Record<string, string> = {
  real_estate: "Real Estate",
  immigration: "Immigration",
  litigation: "Litigation",
  corporate: "Corporate",
  family: "Family",
  criminal: "Criminal",
  wills_estates: "Wills & Estates",
  other: "Other",
};

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const formatCurrencyShort = (cents: number) => {
  const dollars = cents / 100;
  if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(1)}M`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
  return `$${dollars.toFixed(0)}`;
};

const RevenueReport: React.FC<RevenueReportProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-32 animate-pulse">
            <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
            <div className="h-8 w-24 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const totalRevenue = data.byMonth.reduce((s, m) => s + m.totalCents, 0);
  const avgMonthly = data.byMonth.length > 0 ? totalRevenue / data.byMonth.length : 0;
  const topArea = data.byPracticeArea.length > 0 ? data.byPracticeArea[0] : null;

  const summaryCards = [
    {
      label: "Total Revenue",
      value: formatCurrency(totalRevenue),
      icon: <DollarSign className="h-5 w-5" />,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Average Monthly",
      value: formatCurrency(avgMonthly),
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Top Practice Area",
      value: topArea ? PRACTICE_AREA_LABELS[topArea.practiceArea] || topArea.practiceArea : "N/A",
      icon: <Award className="h-5 w-5" />,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  const monthChartData = data.byMonth.map((m) => ({
    month: m.month,
    revenue: m.totalCents / 100,
    count: m.count,
  }));

  const pieChartData = data.byPracticeArea.map((pa) => ({
    name: PRACTICE_AREA_LABELS[pa.practiceArea] || pa.practiceArea,
    value: pa.totalCents / 100,
    color: PRACTICE_AREA_COLORS[pa.practiceArea] || "#64748b",
    count: pa.count,
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Month */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Revenue by Month</h3>
          {monthChartData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <BarChart3 className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No revenue data yet</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Revenue from paid invoices will appear here as your firm processes payments.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  tickFormatter={(v) => formatCurrencyShort(v * 100)}
                />
                <Tooltip
                  formatter={(value: number | string | undefined) => [formatCurrency(Number(value ?? 0) * 100), "Revenue"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar dataKey="revenue" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Revenue by Practice Area */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Revenue by Practice Area</h3>
          {pieChartData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <PieChartIcon className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No practice area breakdown yet</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                As matters are created and invoiced, revenue will be categorized by practice area.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number | string | undefined) => [formatCurrency(Number(value ?? 0) * 100), "Revenue"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Legend
                  formatter={(value: string) => (
                    <span className="text-xs text-slate-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default RevenueReport;
